import { reportShell, reportOptions } from '../../reports/utils/shell';
import { formatDateOnly } from '../../../common/utils/date';
import { PREG_STATE } from '../../../core/constants/dx';
import { TE_MEDICATIONS, TE_ROLE_LABEL, stageName, storageLabel } from '../../../core/constants/transfers';

// Informe PDF de T.E. por OPU (decisión del dueño): el front arma el HTML, el
// backend devuelve el PDF (admin-reports.md). Estructura (pedido del dueño):
//   1. un "informe simple" por sesión (encabezado con fechas estimadas globales
//      dx1/dx2/parto + tabla Donadora · Toro · Receptora),
//   2. al final del todo, el "informe completo" por sesión con las observaciones
//      (CL y OBS) de cada vaca,
//   3. una nota de que las fechas son estimaciones, no determinísticas.
// Fechas: del bloque `forecast` del ensamblado de preñez (0026 §3) —
// dx1_estimated / dx2_estimated / parto_estimated, derivadas de la fecha TE.
// La RAZA de donadora/toro no está en el ensamblado T.E. → se omite por ahora
// (pendiente de contrato).

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const dash = (s) => (s == null || s === '' ? '—' : esc(s));
const date = (d) => (d ? esc(formatDateOnly(d)) : '—');

const MED_LABEL = Object.fromEntries(TE_MEDICATIONS.map((m) => [m.value, m.label]));

const recipientLabel = (r) => (r ? `${r.number || ''}${r.name ? ` · ${r.name}` : ''}`.trim() : '—');
const lineageNames = (t) => {
    const l = t.embryo?.lineage || {};
    return { donor: l.donor?.name || '—', sire: l.sire?.name || '—' };
};

// CL (cuerpo lúteo) legible desde remarks.corpusLuteum = { corpusLuteum:{left,right}, quality, cavernous }.
function fmtCL(remarks) {
    const cl = remarks?.corpusLuteum;
    if (!cl) return '—';
    const parts = [];
    if (cl.corpusLuteum?.left) parts.push('Izq');
    if (cl.corpusLuteum?.right) parts.push('Der');
    if (cl.quality) parts.push(`C${cl.quality}`);
    if (cl.cavernous) parts.push('cav');
    return parts.length ? parts.join(' · ') : '—';
}

// OBS legible desde remarks.details = { state, medication, observations }.
function fmtObs(remarks) {
    const d = remarks?.details;
    if (!d) return '—';
    const parts = [];
    if (d.state) parts.push(d.state);
    if (d.medication) parts.push(MED_LABEL[d.medication] || d.medication);
    if (d.observations) parts.push(d.observations);
    return parts.length ? esc(parts.join(' · ')) : '—';
}

const sessionSub = (te) => `${esc(te.farm?.name || '—')}${te.client?.name ? ` · ${esc(te.client.name)}` : ''} · ${date(te.date)}`;

// El banner de marca solo aparece en la 1ª página (es un <img> en el flujo). Las
// secciones que abren página nueva (break-before) quedarían pegadas al borde
// superior → se les da aire arriba para separarlas de la punta del documento.
const pageStart = 'break-before:page;padding-top:14mm;';

// Informe SIMPLE de una sesión: encabezado con las fechas estimadas globales
// (mismas para toda la sesión) + tabla Donadora · Toro · Receptora.
function simpleSection(te, dates, brk) {
    const transfers = te.transfers || [];
    const staff = (te.staff || []).filter((s) => s.status !== 0)
        .map((s) => `${esc(s.user?.name || '')} (${esc(TE_ROLE_LABEL[s.role] || s.role)})`).join(', ');
    const rows = transfers.map((t, i) => {
        const { donor, sire } = lineageNames(t);
        // estado = estadio de maduración del embrión; preservación = fresco/congelado (storage).
        const e = t.embryo || {};
        const estado = e.stage ? esc(stageName(e.stage)) : '—';
        const preserv = e.storage ? esc(storageLabel(e.storage)) : '—';
        return `<tr><td>${i + 1}</td><td>${esc(donor)}</td><td>${esc(sire)}</td><td>${esc(recipientLabel(t.recipient))}</td><td>${estado}</td><td>${preserv}</td></tr>`;
    }).join('');

    return `<section style="${brk ? pageStart : ''}">
        <h1 class="report-title">Transferencia ${esc(te.code)}</h1>
        <p class="report-subtitle">${sessionSub(te)}${staff ? ` · ${staff}` : ''}</p>
        <div class="meta">
            <div class="meta-item"><strong>Dx 1 estimado</strong><span>${date(dates?.dx1)}</span></div>
            <div class="meta-item"><strong>Dx 2 estimado</strong><span>${date(dates?.dx2)}</span></div>
            <div class="meta-item"><strong>Parto estimado</strong><span>${date(dates?.parto)}</span></div>
            <div class="meta-item"><strong>Transferencias</strong><span>${transfers.length}</span></div>
        </div>
        <table>
            <thead><tr><th>#</th><th>Donadora</th><th>Toro</th><th>Receptora</th><th>Estado</th><th>Preserv.</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="6">Sin transferencias</td></tr>'}</tbody>
        </table>
    </section>`;
}

// Informe COMPLETO de una sesión: detalle con CL y OBS por vaca. Va al final.
function completeSection(te) {
    const transfers = te.transfers || [];
    const rows = transfers.map((t, i) => {
        const { donor, sire } = lineageNames(t);
        const st = PREG_STATE[t.pregnancy?.state];
        return `<tr>
            <td>${i + 1}</td>
            <td>${esc(recipientLabel(t.recipient))}</td>
            <td>${esc(donor)} × ${esc(sire)}</td>
            <td>${fmtCL(t.remarks)}</td>
            <td style="text-align:left;">${fmtObs(t.remarks)}</td>
            <td>${dash(st?.label)}</td>
        </tr>`;
    }).join('');

    return `<section style="${pageStart}">
        <h2 class="report-section-title">Detalle de T.E. ${esc(te.code)} — observaciones</h2>
        <p class="report-subtitle">${sessionSub(te)}</p>
        <table>
            <thead><tr><th>#</th><th>Receptora</th><th>Donadora × Toro</th><th>CL</th><th>OBS</th><th>Preñez</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="6">Sin transferencias</td></tr>'}</tbody>
        </table>
    </section>`;
}

const NOTE = `<p style="margin-top:20px;font-size:10px;color:#6b7280;font-style:italic;line-height:1.5;">
    Nota: las fechas de dx1, dx2 y parto son <strong>estimaciones</strong> calculadas a partir de la fecha de la
    transferencia; no implican que el evento deba ocurrir exactamente ese día (los procesos biológicos no son
    determinísticos y pueden variar según la receptora y la raza).
</p>`;

// items: [{ session: <ensamblado TE>, dates: { dx1, dx2, parto } }]
// opts:  { opuCode, scopeLabel } — para título/subtítulo y nombre de archivo.
export function buildOpuTransferReport(items, opts = {}) {
    const { opuCode, scopeLabel } = opts;
    const list = items.filter(Boolean);

    const simples = list.map((it, i) => simpleSection(it.session, it.dates, i > 0)).join('');
    const completos = list.map((it) => completeSection(it.session)).join('');

    const head = `
        <p class="report-subtitle" style="margin-bottom:14px;">
            Informe de transferencias${opuCode ? ` · OPU ${esc(opuCode)}` : ''}${scopeLabel ? ` · ${esc(scopeLabel)}` : ''}
            · ${list.length} sesión(es)
        </p>`;

    const bodyHtml = `${head}${simples}${completos}${NOTE}`;

    const slug = (scopeLabel || 'te').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const filename = `${opuCode ? `${opuCode}-` : ''}informe-te${slug && slug !== 'te' ? `-${slug}` : ''}`;

    return { html: reportShell({ bodyHtml }), filename, options: reportOptions() };
}
